class CartWorker {
    constructor(storageKey, ajax, customerData, formKey) {
        this.storageKey = storageKey;
        this.ajax = ajax;
        this.customerData = customerData;
        this.formKey = formKey;
        this.minicartButtonElement = document.querySelector(
            ".minicart-wrapper .mini-cart.showcart > div"
        );
        this.loadingClassName = "loading";

        this.sectionsToReload = ['cart'];
        this.executionInterval = 100;
        this.itemsCountChanged = false;
    }

    async run(skipUserCheck = false) {
        if (!skipUserCheck) {
            let userLogged = await this.getIsUserLoggedIn();

            if (!userLogged) {
                return;
            }
        }

        let cartStatusFromLS = await this.getCartStatusFromLocalStorage();

        if (document.hasFocus() && cartStatusFromLS) {
            await this.removeCartStatusFromLocalStorage();
            this.setLoadingClassToCartWrapper(true);
            let currentCartItems = await this.getCurrentCartItems();

            let diff = this.getCartStatusDiff(
                currentCartItems,
                cartStatusFromLS
            );

            this.saveItemsInCartChangedStatus(
                currentCartItems,
                cartStatusFromLS
            );

            let newCartStatusResponse = await this.setNewCartStatus(diff);

            let error = false;

            if (newCartStatusResponse.messages.length) {
                for (let message of newCartStatusResponse.messages) {
                    if (message.type === "error") {
                        error = true;
                        break;
                    }
                }
            }

            if (error) {
                await this.restoreCartStatusInLocalStorage(cartStatusFromLS);
            } else {
                await this.updateMiniCart(newCartStatusResponse);
                this.emitCartChangedEvent();
            }

            this.setLoadingClassToCartWrapper(false);
        }

        setTimeout(async () => {
            await this.run(true);
        }, this.executionInterval);
    }

    async getCurrentCartItems() {
        return await this._makeRequest("graphql_reload_cart/carts/mine", "GET");
    }

    async getCartStatusFromLocalStorage() {
        return await JSON.parse(localStorage.getItem(this.storageKey));
    }

    async removeCartStatusFromLocalStorage() {
        return await localStorage.setItem(this.storageKey, null);
    }

    async restoreCartStatusInLocalStorage(status) {
        let newStatus = {
            ...status
        };

        let currentStatus = await this.getCartStatusFromLocalStorage();

        currentStatus = currentStatus ? currentStatus : {};

        newStatus = {
            ...newStatus,
            ...currentStatus
        };

        return await localStorage.setItem(
            this.storageKey,
            JSON.stringify(newStatus)
        );
    }

    getCartStatusDiff(currentCartStatus, cartStatusFromLocalStorage) {
        let changes = {};

        for (let productId in cartStatusFromLocalStorage) {
            if (cartStatusFromLocalStorage.hasOwnProperty(productId)) {
                let item = cartStatusFromLocalStorage[productId];
                let currentItem = currentCartStatus.find(
                    item => Number(item.product_id) === Number(productId)
                );

                if (
                    (!currentItem && item.qty !== 0) ||
                    (currentItem && item.qty !== Number(currentItem.qty))
                ) {
                    changes[productId] = {
                        ...item,
                        item_id: currentItem ? currentItem.item_id : null
                    };
                }
            }
        }

        return changes;
    }

    async setNewCartStatus(newCartStatus) {
        let requestData = {
            form_key: this.formKey,
            items: []
        };

        for (let productId in newCartStatus) {
            if (newCartStatus.hasOwnProperty(productId)) {
                requestData.items.push({
                    productId,
                    qty: newCartStatus[productId].qty
                });
            }
        }

        return await this._makeRequest(
            "graphql_reload_cart/addtocart/bulk",
            "POST",
            requestData
        );
    }


    async updateMiniCart(cartData) {
        const {
            itemsCount,
            currency,
            totals: {
                subtotal: { value_excl_tax, value_incl_tax }
            }
        } = cartData;

        let miniCartSidebarPrice = document.querySelector(
            ".block.block-minicart .subtotal .amount.price-container .price-including-tax .price"
        );

        if (!miniCartSidebarPrice || itemsCount === 0) {
            await this._makeRequest("graphql_reload_cart/reload/cart");
            return;
        }

        this.minicartButtonElement.children[0].innerText = this._formatPrice(
            value_excl_tax,
            currency
        );
        this.minicartButtonElement.children[1].children[0].innerText = this._formatItemsCount(
            itemsCount
        );
        this.minicartButtonElement.children[1].children[1].innerText = this._formatItemsCount(
            itemsCount
        );
        miniCartSidebarPrice.innerText = this._formatPrice(
            value_incl_tax,
            currency
        );

        this.customerData.invalidate(this.sectionsToReload);
        this.customerData.reload(this.sectionsToReload, true);
    }

    getCustomerInfo() {
        let customer = this.customerData.get("customer");

        return customer();
    }

    isLoggedIn(customerInfo) {
        customerInfo = customerInfo || this.getCustomerInfo();

        return customerInfo && customerInfo.data_id;
    }

    async getIsUserLoggedIn() {
        let customerInfo = this.getCustomerInfo();

        return new Promise(resolve => {
            if (customerInfo && customerInfo.data_id) {
                resolve(this.isLoggedIn(customerInfo));
            } else {
                this.customerData
                    .reload(["customer"], false)
                    .done(() => {
                        resolve(this.isLoggedIn());
                    })
                    .fail(() => {
                        resolve(false);
                    });
            }
        });
    }

    setLoadingClassToCartWrapper(add) {
        if (add) {
            this.minicartButtonElement.classList.add(this.loadingClassName);
        } else {
            this.minicartButtonElement.classList.remove(this.loadingClassName);
        }
    }

    saveItemsInCartChangedStatus(currentCartItems, cartStatusFromLS) {
        this.itemsCountChanged =
            currentCartItems.length !==
            Object.values(cartStatusFromLS).filter(product => product.qty !== 0)
                .length;
    }

    emitCartChangedEvent() {
        if (this.itemsCountChanged) {
            let event = document.createEvent("UIEvents");
            event.initEvent("cart-qty-changed", true, true);
            window.dispatchEvent(event);
        }
    }

    async _makeRequest(url, method = "POST", data = null) {
        return await new Promise((resolve, reject) => {
            this.ajax({
                url: `/${url}`,
                method,
                data,
                success: responseData => {
                    resolve(responseData);
                },
                error: error => {
                    reject(error);
                }
            });
        });
    }

    _formatPrice(number, currency) {
        let newNumber = String(Number(number).toFixed(2)).replace(".", ",");

        newNumber = newNumber
            .split(",")
            .map(number => {
                return number.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
            })
            .join(",");

        return `${newNumber} ${currency}`;
    }

    _formatItemsCount(count) {
        return count > 99 ? "99+" : count;
    }
}

define([], function() {
    return CartWorker;
});
